import { useCallback, useEffect, useMemo, useState } from "react";
import { Camera, ChevronLeft, ChevronRight, MapPin, X } from "lucide-react";
import { carradaFotoUrl } from "@/services/carradas";
import { cn } from "@/utils/cn";

export type CarradaFotoLightboxItem = {
  carradaId: string;
  fotoId: string;
  lado?: string;
  latitude?: number | null;
  longitude?: number | null;
};

type Aba = "fotos" | "localizacao";

type Props = {
  items: CarradaFotoLightboxItem[];
  /** Índice inicial ao abrir (padrão 0). */
  initialIndex?: number;
  onClose: () => void;
};

function coordenadaValida(valor: number | null | undefined): valor is number {
  return typeof valor === "number" && Number.isFinite(valor);
}

function mapaEmbedUrl(latitude: number, longitude: number): string {
  const delta = 0.01;
  const minLon = longitude - delta;
  const minLat = latitude - delta;
  const maxLon = longitude + delta;
  const maxLat = latitude + delta;
  const params = new URLSearchParams({
    bbox: `${minLon},${minLat},${maxLon},${maxLat}`,
    layer: "mapnik",
    marker: `${latitude},${longitude}`,
  });
  return `https://www.openstreetmap.org/export/embed.html?${params.toString()}`;
}

function mapaExternoUrl(latitude: number, longitude: number): string {
  return `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=17/${latitude}/${longitude}`;
}

export function CarradaFotoLightbox({
  items,
  initialIndex = 0,
  onClose,
}: Props) {
  const total = items.length;
  const [aba, setAba] = useState<Aba>("fotos");
  const [index, setIndex] = useState(() =>
    total === 0 ? 0 : Math.min(Math.max(initialIndex, 0), total - 1),
  );

  useEffect(() => {
    if (total === 0) return;
    setIndex((prev) => Math.min(Math.max(prev, 0), total - 1));
  }, [total]);

  const goPrev = useCallback(() => {
    if (total <= 1) return;
    setIndex((i) => (i <= 0 ? total - 1 : i - 1));
  }, [total]);

  const goNext = useCallback(() => {
    if (total <= 1) return;
    setIndex((i) => (i >= total - 1 ? 0 : i + 1));
  }, [total]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (aba !== "fotos") return;
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, goPrev, goNext, aba]);

  const current = total > 0 ? items[index] : null;
  const hasNav = total > 1;
  const temLocal = useMemo(
    () => current != null && coordenadaValida(current.latitude) && coordenadaValida(current.longitude),
    [current],
  );

  if (total === 0 || !current) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3 sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative flex w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-slate-950 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Fotos da carrada"
      >
        <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2.5 sm:px-4">
          <div className="flex flex-1 items-center gap-1 rounded-xl bg-white/5 p-1">
            <button
              type="button"
              className={cn(
                "inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition",
                aba === "fotos" ? "bg-white text-slate-900 shadow" : "text-white/70 hover:bg-white/10 hover:text-white",
              )}
              onClick={() => setAba("fotos")}
            >
              <Camera className="h-4 w-4" />
              Fotos
            </button>
            <button
              type="button"
              className={cn(
                "inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition",
                aba === "localizacao" ? "bg-white text-slate-900 shadow" : "text-white/70 hover:bg-white/10 hover:text-white",
              )}
              onClick={() => setAba("localizacao")}
            >
              <MapPin className="h-4 w-4" />
              Localização
            </button>
          </div>
          <button
            type="button"
            aria-label="Fechar"
            className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative flex min-h-[min(70vh,640px)] items-center justify-center bg-black">
          {aba === "fotos" ? (
            <>
              {hasNav && (
                <button
                  type="button"
                  aria-label="Foto anterior"
                  className="absolute left-2 z-10 rounded-full bg-black/60 p-2 text-white shadow hover:bg-black/80 sm:left-3"
                  onClick={goPrev}
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
              )}

              <img
                src={carradaFotoUrl(current.carradaId, current.fotoId)}
                alt={`Foto da caçamba ${index + 1} de ${total}`}
                className="max-h-[min(70vh,640px)] max-w-full object-contain"
              />

              {hasNav && (
                <button
                  type="button"
                  aria-label="Próxima foto"
                  className="absolute right-2 z-10 rounded-full bg-black/60 p-2 text-white shadow hover:bg-black/80 sm:right-3"
                  onClick={goNext}
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              )}

              {(hasNav || current.lado) && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/65 px-3 py-1 text-xs font-medium text-white tabular-nums">
                  {current.lado ? `${current.lado} · ` : ""}
                  {index + 1} / {total}
                </div>
              )}
            </>
          ) : temLocal ? (
            <div className="flex h-full min-h-[min(70vh,640px)] w-full flex-col">
              <iframe
                title="Mapa da localização da foto"
                src={mapaEmbedUrl(current.latitude!, current.longitude!)}
                className="min-h-0 w-full flex-1 border-0 bg-slate-900"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-white/10 bg-slate-950 px-4 py-3 text-sm text-white/80">
                <div>
                  {current.lado ? <span className="font-semibold text-white">{current.lado}</span> : null}
                  {current.lado ? " · " : ""}
                  {current.latitude!.toFixed(6)}, {current.longitude!.toFixed(6)}
                </div>
                <a
                  className="rounded-lg bg-white/10 px-3 py-1.5 font-semibold text-white hover:bg-white/20"
                  href={mapaExternoUrl(current.latitude!, current.longitude!)}
                  target="_blank"
                  rel="noreferrer"
                >
                  Abrir no mapa
                </a>
              </div>
            </div>
          ) : (
            <div className="flex max-w-md flex-col items-center gap-3 px-6 py-16 text-center">
              <div className="rounded-full bg-white/10 p-3 text-white/80">
                <MapPin className="h-6 w-6" />
              </div>
              <p className="text-base font-semibold text-white">Localização indisponível</p>
              <p className="text-sm text-white/65">
                Esta foto não trouxe coordenada. Nas próximas fotos tiradas pelo celular com a localização
                ligada, o mapa aparece aqui.
              </p>
              {current.lado ? (
                <p className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/80">
                  Lado: {current.lado}
                </p>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
